import { Search, RotateCcw } from "lucide-react";
import { LISTING_TYPES, PROPERTY_TYPES, FACADES, VIEWS } from "@/lib/portfolioOptions";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const EMPTY_FILTERS = {
  q: "", listing_type: "ALL", property_type: "ALL", city: "ALL", facade: "ALL", view: "ALL", eids: "ALL",
  min_price: "", max_price: "", min_area: "", sort: "NEWEST",
};

const SORTS = { NEWEST: "En yeni", PRICE_ASC: "Fiyat (artan)", PRICE_DESC: "Fiyat (azalan)", AREA_DESC: "Alan (büyükten)" };

const norm = (s) => (s || "").toLocaleLowerCase("tr-TR");

export const applyFilters = (items, f) => {
  const q = norm(f.q.trim());
  const out = items.filter((p) =>
    (!q || [p.title, p.city, p.district, p.neighborhood, p.description, p.eids_code, p.owner_name].some((v) => norm(v).includes(q))) &&
    (f.listing_type === "ALL" || p.listing_type === f.listing_type) &&
    (f.property_type === "ALL" || p.property_type === f.property_type) &&
    (f.city === "ALL" || p.city === f.city) &&
    (f.facade === "ALL" || (p.facades || []).includes(f.facade)) &&
    (f.view === "ALL" || (p.views || []).includes(f.view)) &&
    (f.eids === "ALL" || (f.eids === "YES") === !!p.has_eids) &&
    (!f.min_price || p.price >= Number(f.min_price)) &&
    (!f.max_price || p.price <= Number(f.max_price)) &&
    (!f.min_area || p.area_m2 >= Number(f.min_area))
  );
  const sorters = {
    NEWEST: (a, b) => (b.created_at || "").localeCompare(a.created_at || ""),
    PRICE_ASC: (a, b) => a.price - b.price,
    PRICE_DESC: (a, b) => b.price - a.price,
    AREA_DESC: (a, b) => b.area_m2 - a.area_m2,
  };
  return out.sort(sorters[f.sort]);
};

const Pick = ({ value, onChange, all, options, testId, className = "w-36" }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId} className={`h-9 ${className}`}><SelectValue /></SelectTrigger>
    <SelectContent>
      {all && <SelectItem value="ALL">{all}</SelectItem>}
      {Object.entries(options).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const PortfolioFilters = ({ filters, onChange, cities }) => {
  const set = (k) => (v) => onChange({ ...filters, [k]: v?.target ? v.target.value : v });
  return (
    <div data-testid="portfolio-filters" className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input data-testid="portfolio-search-input" value={filters.q} onChange={set("q")} placeholder="Başlık, il, ilçe, mahalle, açıklama, EİDS kodu veya danışman ara..." className="pl-10" />
      </div>
      <div className="flex flex-wrap gap-2 items-center">
        <Pick testId="filter-listing-type" value={filters.listing_type} onChange={set("listing_type")} all="Satılık + Kiralık" options={LISTING_TYPES} />
        <Pick testId="filter-property-type" value={filters.property_type} onChange={set("property_type")} all="Tüm tipler" options={Object.fromEntries(PROPERTY_TYPES.map((t) => [t, t]))} className="w-32" />
        <Pick testId="filter-city" value={filters.city} onChange={set("city")} all="Tüm iller" options={Object.fromEntries(cities.map((c) => [c, c]))} />
        <Pick testId="filter-facade" value={filters.facade} onChange={set("facade")} all="Tüm cepheler" options={FACADES} />
        <Pick testId="filter-view" value={filters.view} onChange={set("view")} all="Tüm manzaralar" options={VIEWS} />
        <Pick testId="filter-eids" value={filters.eids} onChange={set("eids")} all="EİDS fark etmez" options={{ YES: "EİDS var", NO: "EİDS yok" }} className="w-40" />
        <Input data-testid="filter-min-price" type="number" value={filters.min_price} onChange={set("min_price")} placeholder="Min fiyat" className="h-9 w-28" />
        <Input data-testid="filter-max-price" type="number" value={filters.max_price} onChange={set("max_price")} placeholder="Max fiyat" className="h-9 w-28" />
        <Input data-testid="filter-min-area" type="number" value={filters.min_area} onChange={set("min_area")} placeholder="Min m²" className="h-9 w-24" />
        <Pick testId="filter-sort" value={filters.sort} onChange={set("sort")} options={SORTS} className="w-40" />
        <Button data-testid="filter-reset-btn" variant="ghost" size="sm" onClick={() => onChange(EMPTY_FILTERS)}><RotateCcw className="w-3.5 h-3.5 mr-1" />Temizle</Button>
      </div>
    </div>
  );
};
